import Page from "@/lib/pages/Page";
import {Platform, Pressable, StyleSheet, View} from "react-native";
import {useChat} from "@/lib/providers/ChatProvider";
import SpinnerPage from "@/lib/pages/SpinnerPage";
import {use, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useAccount} from "@/lib/providers/AccountProvider";
import {
    KeyboardAwareScrollView,
    KeyboardAwareScrollViewRef,
    KeyboardStickyView
} from "react-native-keyboard-controller";
import ChatInput from "@/lib/components/ChatInput";
import ChatBubble from "@/lib/components/ChatBubble";
import Carousel from "@/lib/components/Carousel";
import {chat} from "@/lib/proto";
import PrimaryText from "@/lib/components/PrimaryText";
import {Color} from "@/lib/styles";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {TabBarContext} from "@/lib/components/TabBarContext";
import {useFocusEffect} from "expo-router";
import Avatar = chat.Avatar;
import ChatMessage = chat.ChatMessage;
import SendChatMessageRequest = chat.SendChatMessageRequest;
import {useEvent} from "@/lib/providers/EventProvider";
import NavButton from "@/lib/components/NavButton";

type Props = {
    chatId: string,
}

export default function ChatPage({chatId}: Props) {
    const {chats, chatError, markMessageAsRead} = useChat();
    const {account} = useAccount();
    const {event} = useEvent();

    const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);

    const {setIsTabBarHidden} = use(TabBarContext);

    const [previewThumbnail, setPreviewThumbnail] = useState<string | null>(null);
    const [numShownMessages, setNumShownMessages] = useState(100);
    const [scrolledToBottom, setScrolledToBottom] = useState(true);

    useFocusEffect(() => {
        setIsTabBarHidden(true);
        if (scrollRef.current && scrolledToBottom && lastMessage) {
            // autoscroll to bottom on mobile after loading page
            setTimeout(scrollRef.current.scrollToEnd, 20);
        }
        return () => setIsTabBarHidden(false);
    });

    const currentChat = useMemo(() => {
        return chats.get(chatId);
    }, [chatId, chats])

    const messages = useMemo(() => {
        if (!currentChat) {
            return [];
        }
        const result: ChatMessage[] = [];
        currentChat.messages.forEach(value => {
            result.push(value);
        });
        return result.slice(-numShownMessages);
    }, [currentChat, numShownMessages])

    const pendingMessages = useMemo(() => {
        if (!currentChat) {
            return [];
        }
        const result: SendChatMessageRequest[] = [];
        currentChat.pendingMessages.forEach(msg => {
            if (msg.reaction.length === 0) {
                result.push(msg);
            }
        })
        return result;
    }, [currentChat])

    const numTruncatedMessages = useMemo(() => {
        return (currentChat?.messages.size || 0) - messages.length;
    }, [currentChat, messages]);

    const lastMessage = useMemo(() => {
        if (messages.length === 0) {
            return;
        }
        // console.debug("Calculating last message in chat", chatId, messages[messages.length - 1]);
        return messages[messages.length - 1];
    }, [messages]);

    const reactions = useCallback((messageID: string) => {
        return currentChat?.reactions.get(messageID) || new Map<string, Avatar[]>();
    }, [currentChat])

    const allThumbnails = useMemo(() => {
        return messages
            .flatMap(msg => msg.thumbnailIds)
            .filter(t => t.length > 0);
    }, [messages]);

    useEffect(() => {
        // could trigger also on currentChat?.reactions
        if (!scrollRef.current || !scrolledToBottom || !lastMessage) {
            return;
        }
        scrollRef.current.scrollToEnd();
        markMessageAsRead(lastMessage.chatId, lastMessage.messageId);
    }, [scrollRef, lastMessage, pendingMessages, scrolledToBottom, markMessageAsRead]);

    if (!currentChat || !account) {
        return <SpinnerPage/>
    }

    // Determine if event ref or truncated messages should stick
    const hasEventRef = !event && !!currentChat.chat.event;
    const hasTruncatedMessages = numTruncatedMessages > 0;
    const stickyHeaderIndices = (() => {
        if (hasEventRef && hasTruncatedMessages) {
            return [0, 1];
        } else if (hasEventRef || hasTruncatedMessages) {
            return [0];
        } else {
            return undefined;
        }
    })

    return (
        <Page scrollable={false}>
            <View style={styles.pageWrapper}>
                <Carousel thumbnailId={previewThumbnail} thumbnails={allThumbnails}
                          onThumbnailClick={setPreviewThumbnail}/>
                <KeyboardAwareScrollView
                    // refreshControl={}
                    maintainVisibleContentPosition={{
                        minIndexForVisible: 0,
                    }}
                    ref={scrollRef}
                    focusable={false}
                    indicatorStyle={"white"}
                    persistentScrollbar={false}
                    contentInsetAdjustmentBehavior={"always"}
                    // automaticallyAdjustKeyboardInsets={true}
                    contentContainerStyle={styles.chatLog}
                    stickyHeaderIndices={stickyHeaderIndices()}
                    stickyHeaderHiddenOnScroll={true}
                    removeClippedSubviews={true}
                    onScroll={(event) => {
                        const {layoutMeasurement, contentOffset, contentSize} = event.nativeEvent;
                        // console.debug(event.nativeEvent);
                        const isBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height;
                        if (isBottom && lastMessage !== undefined) {
                            markMessageAsRead(lastMessage.chatId, lastMessage.messageId);
                        }
                        if (!scrolledToBottom && isBottom) {
                            setScrolledToBottom(true);
                        } else if (scrolledToBottom && !isBottom) {
                            setScrolledToBottom(false);
                        }
                    }}
                >
                    {!event && !!currentChat.chat.event &&
                        <View style={styles.topNav}>
                            <NavButton
                                href={`/e/${currentChat.chat.event.id}`}
                                title={currentChat.chat.event.title || undefined}
                                icon={"event"}
                            />
                        </View>
                    }
                    {numTruncatedMessages > 0 &&
                        <Pressable style={styles.truncatedBanner}
                                   onPress={() => setNumShownMessages(numShownMessages + 100)}>
                            <PrimaryText>{numTruncatedMessages} tidligere meldinger</PrimaryText>
                        </Pressable>
                    }
                    {messages.map(message =>
                        <ChatBubble
                            key={message.messageId}
                            message={message}
                            onPressThumbnail={setPreviewThumbnail}
                            reactions={reactions(message.messageId)}
                        />
                    )}
                    {!!chatError &&
                        <ErrorMessage error={chatError}/>
                    }
                    {pendingMessages.length > 0 &&
                        <View style={styles.pendingMessages}>
                            {pendingMessages.map(message =>
                                <ChatBubble
                                    key={message.clientMessageId}
                                    message={message}
                                    reactions={new Map()}
                                    onPressThumbnail={setPreviewThumbnail}
                                />
                            )}
                        </View>
                    }
                </KeyboardAwareScrollView>
                <KeyboardStickyView style={styles.inputContainer}>
                    <ChatInput
                        autofocus={Platform.OS === "web"}
                        chat={currentChat.chat}
                    />
                </KeyboardStickyView>
            </View>
        </Page>
    )
}

export const styles = StyleSheet.create({
    topNav: {
        backgroundColor: Color.Background,
        paddingBottom: 6,
        borderRadius: 8,
    },
    truncatedBanner: {
        flexDirection: "row",
        justifyContent: "center",
        alignContent: "center",
        backgroundColor: Color.BackgroundContainer,
        padding: 8,
        borderRadius: 8,
    },
    chatLog: {
        flexDirection: "column",
        gap: 4,
    },
    pendingMessages: {
        opacity: 0.4,
        gap: 4,
    },
    pageWrapper: {
        flex: 1,
        marginBottom: Platform.OS !== "web" ? 16 : 0,
    },
    inputContainer: {
        backgroundColor: Color.Background,
        paddingTop: 4,
    }
})